import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { WEDDING_BANDS_QUERY } from '../lib/queries';
import useStore from '../store/useStore';
import { useScrollEngine } from '../lib/scroll/useScrollEngine';
import NeoCanvas from '../components/three/NeoCanvas';
import HeroSection from '../components/landing/HeroSection';
import StorySections from '../components/landing/StorySections';
import DarkModeMarker from '../components/landing/DarkModeMarker';
import SimpleConfigurator from '../components/configurator/SimpleConfigurator';
import DarkModeToggle from '../components/ui/DarkModeToggle';
import type { ApiRingTemplate } from '../types/api';

/**
 * Main (and only) page of hd_neo.
 *
 * Architecture:
 *   ┌─ NeoCanvas (fixed z=0, pointer-events none)
 *   │    └─ RingScene → WeddingBandModels + CameraRig + PostEffects
 *   └─ #page-content (relative z=1, scrollable)
 *        ├─ HeroSection          (scroll 0–0.2)
 *        ├─ StorySections        (scroll 0.2–0.8)
 *        ├─ DarkModeMarker       (fires at darkModeThreshold ≈ 0.55)
 *        └─ SimpleConfigurator   (scroll 0.8–1.0)
 */
export default function LandingPage() {
    // 1. Initialise smooth scroll + camera drive
    useScrollEngine();

    // 2. Fetch ring templates from hd_erp
    const { data } = useQuery<{ ringTemplatesByGroupAndConfigurator: ApiRingTemplate[] }>(
        WEDDING_BANDS_QUERY,
        {
            variables: {
                productGroup: ['wedding_ring'],
                configurator: true,
            },
        },
    );
    const { setRingTemplates } = useStore.getState().methods;

    useEffect(() => {
        if (data?.ringTemplatesByGroupAndConfigurator) {
            setRingTemplates(data.ringTemplatesByGroupAndConfigurator);
        }
    }, [data, setRingTemplates]);

    // 3. Fade-in story beat text on scroll into view
    useEffect(() => {
        const beats = document.querySelectorAll<HTMLElement>('.story-beat-text');

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        entry.target.classList.remove('opacity-0', 'translate-y-6');
                        observer.unobserve(entry.target);
                    }
                }
            },
            { threshold: 0.3 },
        );

        beats.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <>
            {/* Fixed 3D canvas */}
            <NeoCanvas />

            {/* Dark mode toggle */}
            <DarkModeToggle />

            {/* Scrollable HTML content */}
            <div id="page-content">
                {/* Dark mode scroll position sentinel */}
                <div style={{ position: 'relative' }}>
                    <DarkModeMarker />
                </div>

                <HeroSection />
                <StorySections />
            </div>

            {/* Configurator — fixed overlay, slides in at end of scroll */}
            <SimpleConfigurator />
        </>
    );
}
